<template>
  <div class="nb-dashboard-guia pb-5">
    <!-- HERO / ONBOARDING PROGRESS BANNER -->
    <div class="nb-hero-card mb-4">
      <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
        <div class="d-flex flex-column">
          <div class="d-flex align-items-center mb-1">
            <div class="nb-hero-icon mr-2">
              <i class="fa fa-rocket text-primary"></i>
            </div>
            <h3 class="nb-hero-title mb-0">Guia de Primeiros Passos</h3>
          </div>
          <p class="nb-hero-subtitle mb-0">
            Siga as 6 etapas essenciais abaixo para configurar sua empresa e começar a emitir notas com rapidez e conformidade fiscal.
          </p>
        </div>

        <div class="d-flex align-items-center gap-3">
          <button
            type="button"
            class="btn nb-btn-guide-toggle"
            @click="apiGuiaUsuario"
          >
            <i class="fa" :class="showGuia ? 'fa-eye-slash' : 'fa-eye'"></i>
            <span>{{ showGuia ? 'Não exibir este guia ao logar' : 'Exibir este guia ao logar' }}</span>
          </button>
        </div>
      </div>

      <!-- PROGRESS BAR -->
      <div class="nb-progress-wrapper mt-4">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="nb-progress-label">
            Progresso de Configuração
          </span>
          <span class="nb-progress-percent">
            {{ completedCount }} de 6 etapas concluídas ({{ progressPercent }}%)
          </span>
        </div>
        <div class="progress nb-progress-bar-container">
          <div
            class="progress-bar nb-progress-bar-fill"
            role="progressbar"
            :style="{ width: progressPercent + '%' }"
            :aria-valuenow="progressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </div>

    <!-- 6 STEP CHECKLIST CARDS -->
    <div class="row mb-5">
      <div
        class="col-xl-4 col-md-6 col-12 mb-4"
        v-for="(item, index) in guiaNfe"
        :key="index"
      >
        <div class="nb-step-card" :class="{ 'step-completed': item.completed }">
          <div class="nb-step-card-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <span class="nb-step-number mr-2">{{ index + 1 }}</span>
              <span v-if="item.completed" class="badge badge-success-soft">
                <i class="fa fa-check mr-1"></i> Concluído
              </span>
              <span v-else class="badge badge-pending-soft">
                <i class="fa fa-clock-o mr-1"></i> Pendente
              </span>
            </div>
            <button
              v-if="item.video"
              :id="item.name + '_video'"
              class="btn nb-btn-video ml-auto"
              @click="handleShowVideo(item)"
              title="Assistir tutorial em vídeo"
              aria-label="Assistir tutorial em vídeo"
            >
              <i class="fa fa-youtube-play"></i>
            </button>
          </div>

          <!-- STEP SVG ILLUSTRATION -->
          <div class="nb-step-illustration mt-3 mb-2">
            <guia-step-svg :step="item.name" :completed="item.completed"></guia-step-svg>
          </div>

          <div class="nb-step-card-body">
            <h5 class="nb-step-title">{{ item.title }}</h5>
            <p class="nb-step-desc">
              {{ getStepDescription(item.name) }}
            </p>
          </div>

          <div class="nb-step-card-footer">
            <button
              class="btn btn-block"
              :class="item.completed ? 'nb-btn-view' : 'nb-btn-action'"
              @click="handleFazerAgora(item)"
            >
              <i class="fa" :class="item.completed ? 'fa-check' : 'fa-arrow-right'" aria-hidden="true"></i>
              <span class="ml-1">{{ item.completed ? 'Configuração Concluída' : 'Configurar Agora' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SEÇÃO: VÍDEOS E TREINAMENTOS -->
    <div class="nb-section-header mb-3">
      <h4 class="nb-section-title">
        <i class="fa fa-video-camera text-primary mr-2"></i> Tutoriais e Treinamentos em Vídeo
      </h4>
      <p class="nb-section-subtitle">
        Aprenda a utilizar os recursos de Frente de Caixa (PDV), importações de XML e cadastros em massa.
      </p>
    </div>

    <div class="row">
      <div
        class="col-xl-4 col-md-6 col-12 mb-3"
        v-for="(item, index) in allTutorials"
        :key="index"
      >
        <div class="nb-tutorial-card">
          <div class="d-flex align-items-start justify-content-between p-3">
            <div
              class="d-flex align-items-center nb-tutorial-link"
              @click="handleShowVideo(item)"
              @keyup.enter="handleShowVideo(item)"
              role="button"
              tabindex="0"
              title="Assistir tutorial em vídeo"
            >
              <div class="nb-tutorial-icon mr-3">
                <i class="fa fa-youtube-play text-danger"></i>
              </div>
              <div>
                <h6 class="nb-tutorial-title mb-1">{{ item.title }}</h6>
                <span class="nb-tutorial-tag" v-if="item.subtitle">{{ item.subtitle }}</span>
              </div>
            </div>
          </div>
          <div class="nb-tutorial-footer p-2 d-flex justify-content-between align-items-center">
            <button
              class="btn btn-sm nb-btn-play-sm"
              @click="handleShowVideo(item)"
            >
              <i class="fa fa-play mr-1"></i> Assistir Vídeo
            </button>
            <button
              v-if="item.url"
              class="btn btn-sm nb-btn-open-sm"
              @click="handleFazerAgora(item)"
            >
              Abrir tela <i class="fa fa-external-link ml-1"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE VÍDEO -->
    <transition name="fade">
      <div v-if="currVideo" class="nb-video-backdrop" @click="handleCloseVideo">
        <div class="nb-video-modal shadow-lg" @click.stop>
          <div class="nb-video-modal-header d-flex justify-content-between align-items-center px-3 py-3">
            <div class="d-flex align-items-center">
              <div class="nb-modal-header-icon mr-2">
                <i class="fa fa-play-circle text-primary"></i>
              </div>
              <h5 class="nb-modal-header-title mb-0">Tutorial em Vídeo</h5>
            </div>
            <button
              type="button"
              class="close nb-modal-close"
              @click="handleCloseVideo"
              aria-label="Fechar"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="p-3">
            <div class="embed-responsive embed-responsive-16by9 rounded overflow-hidden position-relative nb-video-frame-container">
              <!-- SPINNER / LOADING EFFECT NO PADRÃO DO SISTEMA -->
              <transition name="fade">
                <div v-if="isVideoLoading" class="nb-video-loading">
                  <div class="nb-video-spinner-wrapper mb-3">
                    <i class="fa fa-circle-o-notch fa-spin nb-video-spinner"></i>
                  </div>
                  <span class="nb-video-loading-title">Carregando tutorial...</span>
                  <span class="nb-video-loading-subtitle">Aguarde um instante</span>
                </div>
              </transition>
              <iframe
                class="embed-responsive-item"
                :src="currVideo"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                @load="onVideoLoad"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { apiORDS, apiORDSapp } from "../shared/api-functions";
import { SysVars } from "../main";
import GuiaStepSvg from "./guia-step-svg.vue";

export default {
  name: "dashboard-guia-nfe",

  components: {
    GuiaStepSvg
  },

  data() {
    return {
      guiaNfe: [
        {
          completed: false,
          name: "guianfe_empresa",
          title: "Configure os dados da sua empresa",
          subtitle: "empresa",
          video: "//www.youtube.com/embed/0QMVqSEm-ww",
          url: "/guiaempresa"
        },
        {
          completed: false,
          name: "guianfe_certificado",
          title: "Configure o certificado digital",
          subtitle: "digital",
          video: "//www.youtube.com/embed/SxQYdDhYs7g",
          url: "/filiallist/certificado"
        },
        {
          completed: false,
          name: "guianfe_item",
          title: "Cadastre produtos e serviços",
          subtitle: "produto",
          video: "//www.youtube.com/embed/c241at1ws14",
          url: "/item/novo"
        },
        {
          completed: false,
          name: "guianfe_part",
          title: "Cadastre clientes e fornecedores",
          subtitle: "cliente",
          video: "//www.youtube.com/embed/Rb8ygl4dAqk",
          url: "/part/novo"
        },
        {
          completed: false,
          name: "guianfe_venda",
          title: "Emita um pedido de venda ou venda no PDV",
          subtitle: "venda / varejo",
          video: "//www.youtube.com/embed/bSJxRmoZePA",
          url: "/pedido/novo"
        },
        {
          completed: false,
          name: "guianfe_nf",
          title: "Emita sua primeira Nota Fiscal",
          subtitle: "nota fiscal",
          video: "//www.youtube.com/embed/kxjxOHP8rRs",
          url: "/nf/novo"
        }
      ],

      vendasVideos: [
        {
          completed: false,
          title: "Frente de Caixa PDV",
          subtitle: "Varejo",
          video: "//www.youtube.com/embed/Du4G_yz1VB8?si=nMyneZI2gx728MYf",
          url: "/varejolist"
        }
      ],

      maisVideos: [
        {
          completed: false,
          title: "Como importar cadastros via XML emitidos",
          subtitle: "Importação",
          video: "//www.youtube.com/embed/IGPuKHAb_Kk",
          url: "/importaxml"
        },
        {
          completed: false,
          title: "Como importar clientes e fornecedores",
          subtitle: "Importação",
          video: "//www.youtube.com/embed/ql3fiqp-Lvk",
          url: "/importa/clientes"
        },
        {
          completed: false,
          title: "Como importar cadastro de produtos",
          subtitle: "Importação",
          video: "//www.youtube.com/embed/6Bmo4YZTmII",
          url: "/importa/produtos"
        },
        {
          completed: false,
          title: "Como lançar um pedido de venda",
          subtitle: "Vendas",
          video: "//www.youtube.com/embed/oDczqdHbDUc",
          url: "/pedido/novo"
        },
        {
          completed: false,
          title: "Configuração de regras fiscais de ICMS",
          subtitle: "Fiscal",
          video: "//www.youtube.com/embed/gjwdNe3q8AM",
          url: "/regraimplist"
        }
      ],

      currVideo: null,
      isVideoLoading: false,
    };
  },

  computed: {
    sessao: function() {
      return this.$store.getters.getSessao;
    },

    dadosGuia: function() {
      return this.$store.getters.getGuiaNFe || {};
    },

    showGuia: function() {
      return SysVars.guia;
    },

    completedCount: function() {
      return this.guiaNfe.filter(item => item.completed).length;
    },

    progressPercent: function() {
      return Math.round((this.completedCount / 6) * 100);
    },

    allTutorials: function() {
      return [...this.vendasVideos, ...this.maisVideos];
    }
  },

  methods: {
    getStepDescription(name) {
      switch (name) {
        case "guianfe_empresa":
          return "Defina CNPJ, Razão Social, Regime Tributário e inscrições fiscais.";
        case "guianfe_certificado":
          return "Vincule seu certificado digital A1 ou A3 para assinatura e transmissão SEFAZ.";
        case "guianfe_item":
          return "Adicione itens com NCM, tributação de ICMS/PIS/COFINS e unidades de medida.";
        case "guianfe_part":
          return "Cadastre seus clientes com endereço, CPF/CNPJ e dados para faturamento.";
        case "guianfe_venda":
          return "Monte pedidos de venda com cálculo fiscal ou realize vendas rápidas no Frente de Caixa (PDV).";
        case "guianfe_nf":
          return "Transmita sua primeira NF-e mercantil ou cupom NFC-e para validação SEFAZ.";
        default:
          return "";
      }
    },

    handleShowVideo(item) {
      this.isVideoLoading = true;
      this.currVideo = item.video;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
      this.videoLoadTimeout = setTimeout(() => {
        this.isVideoLoading = false;
      }, 7000);
    },

    handleCloseVideo() {
      this.currVideo = null;
      this.isVideoLoading = false;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
    },

    onVideoLoad() {
      this.isVideoLoading = false;
      if (this.videoLoadTimeout) {
        clearTimeout(this.videoLoadTimeout);
      }
    },

    handleFazerAgora(item) {
      if (item.name === "guianfe_venda") {
        if (item.completed && !this.dadosGuia.pedido && this.dadosGuia.varejo) {
          this.$router.push("/varejolist");
          return;
        }
        if (SysVars.appVarejo && !SysVars.appERP) {
          this.$router.push("/varejolist");
          return;
        }
      }
      if (item.url) {
        this.$router.push(item.url);
      }
    },

    async loadData() {
      await this.$store.dispatch("loadSessao", this);
      await this.$store.dispatch("loadGuiaNFe", this);

      this.guiaNfe[0].completed = this.dadosGuia.empresa;
      const hasCertSessao = Boolean(this.sessao && Array.isArray(this.sessao.certificado) && this.sessao.certificado.length > 0);
      this.guiaNfe[1].completed = Boolean(this.dadosGuia.certificado || hasCertSessao);
      this.guiaNfe[2].completed = this.dadosGuia.item;
      this.guiaNfe[3].completed = this.dadosGuia.cliente;
      this.guiaNfe[4].completed = !!(this.dadosGuia.pedido || this.dadosGuia.varejo);
      this.guiaNfe[5].completed = this.dadosGuia.nf;
    },

    async apiGuiaUsuario() {
      await apiORDSapp(this, "usuario/guia/", "post", null);
      this.$appLoading(false);
      if (SysVars.guia) {
        this.$erpToastSuccess("Guia desativado na tela inicial.");
      } else {
        this.$erpToastSuccess("Guia ativado na tela inicial.");
      }
      SysVars.guia = !SysVars.guia;
    }
  },

  async mounted() {
    await this.loadData();

    var steps = [];

    if (
      this.guiaNfe.filter(
        item => item.name === "guianfe_empresa" && !item.completed
      ).length > 0
    ) {
      steps = [
        {
          selector: "",
          step: 1,
          title: `Olá! Seja muito bem-vindo(a) ao Nota Brasil!`,
          content:
            "Nossa plataforma foi desenvolvida para simplificar o seu dia a dia e aumentar a eficiência da sua empresa.</br>Para te ajudar a dar os primeiros passos, vamos iniciar o tour guiado."
        },
        {
          selector: "#guianfe_empresa_video",
          step: 2,
          title: `Configure os dados da sua empresa`,
          content:
            "Simplifique a configuração do seu cadastro e ganhe tempo! Nossa videoaula te guiará passo a passo por todo o processo."
        }
      ];
    }

    if (this.showGuia && steps.length > 0) {
      try {
        var tourguide = new Tourguide({ steps: steps });
        tourguide.start();
      } catch (e) {
        // tourguide silent fallback
      }
    }
  },

  beforeDestroy() {
    if (this.videoLoadTimeout) {
      clearTimeout(this.videoLoadTimeout);
    }
  }
};
</script>

<style scoped>
.nb-dashboard-guia {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0b1c30;
}

/* HERO CARD */
.nb-hero-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.nb-hero-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.nb-hero-title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-hero-subtitle {
  font-size: 13.5px;
  color: #64748b;
  max-width: 720px;
  line-height: 1.5;
}

.nb-btn-guide-toggle {
  background: #f8fafc;
  color: #004e94;
  border: 1px solid #cbd5e1;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.nb-btn-guide-toggle:hover {
  background: #eff4ff;
  border-color: #004e94;
}

/* PROGRESS */
.nb-progress-wrapper {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 12px 16px;
}

.nb-progress-label {
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.nb-progress-percent {
  font-size: 12.5px;
  font-weight: 700;
  color: #004e94;
}

.nb-progress-bar-container {
  height: 8px;
  border-radius: 10px;
  background: #e2e8f0;
}

.nb-progress-bar-fill {
  background: #004e94;
  border-radius: 10px;
  transition: width 0.4s ease;
}

/* STEP CARDS */
.nb-step-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  transition: all 0.2s ease;
}

.nb-step-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.nb-step-card.step-completed {
  border-color: #a7f3d0;
  background: #fafdfb;
}

.nb-step-number {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #eff4ff;
  color: #004e94;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge-success-soft {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.badge-pending-soft {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.nb-step-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.nb-btn-video {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.12);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nb-btn-video .fa-youtube-play {
  color: #ef4444;
  font-size: 20px;
  line-height: 1;
  transition: transform 0.2s ease, color 0.2s ease;
}

.nb-btn-video:hover {
  background: #ef4444;
  border-color: #dc2626;
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(239, 68, 68, 0.35);
  transform: translateY(-2px) scale(1.05);
}

.nb-btn-video:hover .fa-youtube-play {
  color: #ffffff;
  transform: scale(1.1);
}

.nb-btn-video:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
}

.nb-step-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 6px 10px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.nb-step-card:hover .nb-step-illustration {
  background: #eff4ff;
  border-color: #dbeafe;
}

.nb-step-card.step-completed .nb-step-illustration {
  background: #f0fdf4;
  border-color: #dcfce7;
}

.nb-step-title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #0b1c30;
  margin-top: 12px;
  margin-bottom: 6px;
}

.nb-step-desc {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
  margin-bottom: 16px;
}

.nb-btn-action {
  background: #004e94;
  color: #ffffff;
  border: none;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-action:hover {
  background: #00376c;
  color: #ffffff;
}

.nb-btn-view {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-view:hover {
  background: #d1fae5;
  color: #047857;
}

/* SECTION TUTORIALS */
.nb-section-title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-section-subtitle {
  font-size: 13px;
  color: #64748b;
}

.nb-tutorial-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.nb-tutorial-link {
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  transition: opacity 0.15s ease;
  outline: none;
}

.nb-tutorial-link:hover .nb-tutorial-title {
  color: #004e94;
  text-decoration: underline;
}

.nb-tutorial-link:hover .nb-tutorial-icon {
  transform: scale(1.15);
}

.nb-tutorial-icon {
  font-size: 32px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.nb-tutorial-title {
  font-size: 13px;
  font-weight: 600;
  color: #0b1c30;
  transition: color 0.15s ease;
}

.nb-tutorial-tag {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

.nb-tutorial-footer {
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.nb-btn-play-sm {
  background: transparent;
  color: #004e94;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 6px;
}

.nb-btn-open-sm {
  background: transparent;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 6px;
}

.nb-btn-open-sm:hover {
  color: #004e94;
}

/* MODAL */
.nb-video-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(11, 28, 48, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.nb-video-modal {
  width: 90%;
  max-width: 700px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.nb-video-modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.nb-modal-header-icon {
  font-size: 18px;
  color: #004e94;
  display: flex;
  align-items: center;
}

.nb-modal-header-title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-modal-close {
  font-size: 20px;
  color: #64748b;
  opacity: 0.7;
  transition: all 0.15s ease;
  line-height: 1;
}

.nb-modal-close:hover {
  color: #0b1c30;
  opacity: 1;
}

.nb-video-frame-container {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.nb-video-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: #f8fafc;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.nb-video-spinner-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #eff4ff;
  border: 1px solid #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nb-video-spinner {
  font-size: 24px;
  color: #004e94;
}

.nb-video-loading-title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  color: #0b1c30;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.1px;
}

.nb-video-loading-subtitle {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
}
</style>



// WEBPACK FOOTER //
// src/dashboards/dashboard-guia-nfe.vue